import React, { useEffect, useState } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Chip, CircularProgress, Alert,
  IconButton, Stack, Divider, useMediaQuery
} from '@mui/material';
import { ArrowBack, Receipt, LocalShipping } from '@mui/icons-material';
import { getNotaById } from '../API/notaPengiriman';

const statusColor = (s) => {
  if (s === 'Diterima') return 'success';
  if (s === 'Ditolak') return 'error';
  return 'warning';
};

const PhotoSection = ({ label, url }) => {
  if (!url) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>{label}</Typography>
      <Box
        component="img"
        src={url}
        alt={label}
        sx={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 2, border: '1px solid #e2e8f0', cursor: 'pointer' }}
        onClick={() => openInAppFilePreview({ url: url })}
      />
    </Box>
  );
};

const ExecutiveNotaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:800px)');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getNotaById(id);
        setData(res.data?.data || res.data);
      } catch (err) {
        setError('Gagal memuat data nota');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchData();
  }, [id]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (error || !data) {
    return <Alert severity="error" sx={{ m: 2 }}>{error || 'Data tidak ditemukan'}</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} size="small"><ArrowBack /></IconButton>
        <Box>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={700}>
            Detail Nota Pengiriman
          </Typography>
          <Typography variant="caption" color="text.secondary">#{data.id} — {data.nomorNota || 'Tanpa nomor'}</Typography>
        </Box>
      </Stack>

      <Card sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip size="small" color={statusColor(data.status)} label={data.status || 'Dikirim'} />
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Tanggal Kirim</Typography>
              <Typography variant="body2" fontWeight={600}>{data.tanggalKirim || '-'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Bantuan</Typography>
              <Typography variant="body2" fontWeight={600}>#{data.BantuanId} — {data.Bantuan?.JenisBantuan || '-'}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="text.secondary">Supplier</Typography>
              <Typography variant="body2" fontWeight={600}>{data.SupplierBarang?.namaSupplier || '-'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Catatan</Typography>
              <Typography variant="body2">{data.catatan || '-'}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <Receipt color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700}>Dokumentasi</Typography>
          </Stack>

          <PhotoSection label="Foto Nota" url={data.fotoNotaUrl} />
          <PhotoSection label="Foto Pengiriman" url={data.fotoPengirimanUrl} />
          <PhotoSection label="Foto Barang" url={data.fotoBarangUrl} />

          {data.fotoBarangLainnyaUrl?.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Foto Barang Lainnya</Typography>
              <Grid container spacing={1}>
                {data.fotoBarangLainnyaUrl.map((url, i) => (
                  <Grid item xs={6} sm={4} key={i}>
                    <Box
                      component="img"
                      src={url}
                      alt={`Barang ${i + 1}`}
                      sx={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 2, cursor: 'pointer' }}
                      onClick={() => openInAppFilePreview({ url: url })}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <LocalShipping fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              TFL: {data.Bantuan?.TFL?.username || '-'} | Dibuat oleh: {data.Admin?.username || '-'}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ExecutiveNotaDetail;
